import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
} from "@mui/material";
import { z } from "zod";
import { AxiosError } from "axios";
import {
  Organization,
  CreateOrganizationData,
  EditOrganizationData,
  createOrganization,
  editOrganization,
  disableOrganization,
  enableOrganization,
} from "../../api/organization";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Chip from "@mui/material/Chip";

// Validation schema
const organizationValidationSchema = z.object({
  name: z.string().min(1, "Organization name is required"),
});

interface OrganizationDialogProps {
  open: boolean;
  organization: Organization | null;
  onClose: (refresh?: boolean) => void;
  onDelete?: (organizationId: string) => void;
  onDisableEnable?: (organizationId: string, newDisabledState: boolean) => void;
}

export const OrganizationDialog: React.FC<OrganizationDialogProps> = ({
  open,
  organization,
  onClose,
  onDelete,
  onDisableEnable,
}) => {
  const isEditMode = !!organization?.id;

  // Initial state for a new organization
  const initOrganizationState: Partial<Organization> = {
    name: "",
    mode_allow_create: false,
    mode_allow_translate: false,
    mode_allow_ai_generation: false,
    mode_allow_translate_ai: false,
  };

  const [editingOrganization, setEditingOrganization] = useState<
    Partial<Organization>
  >(initOrganizationState);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [isSaving, setIsSaving] = useState(false);
  const [showDisableEnableConfirm, setShowDisableEnableConfirm] =
    useState(false);

  // Sync local state with prop changes
  useEffect(() => {
    if (organization) {
      setEditingOrganization({ ...organization });
    } else {
      setEditingOrganization(initOrganizationState);
    }
    setValidationErrors({});
  }, [organization, open]);

  const handleFieldChange = (field: string, value: string | boolean) => {
    setEditingOrganization((prev) => {
      const updated = { ...prev, [field]: value };

      // If unchecking "Create", also uncheck "Enable AI" since it depends on Create
      if (field === "mode_allow_create" && !value) {
        updated.mode_allow_ai_generation = false;
      }

      // If unchecking "Translate", also uncheck "AI Translation" since it depends on Translate
      if (field === "mode_allow_translate" && !value) {
        updated.mode_allow_translate_ai = false;
      }

      return updated;
    });

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleSave = async () => {
    try {
      // Validate
      const validationResult =
        organizationValidationSchema.safeParse(editingOrganization);

      if (!validationResult.success) {
        const errors: Record<string, string> = {};
        validationResult.error.issues.forEach((issue) => {
          const path = issue.path.join(".");
          if (path) {
            errors[path] = issue.message;
          }
        });
        setValidationErrors(errors);
        return;
      }

      setIsSaving(true);

      if (isEditMode && editingOrganization.id) {
        // Edit existing organization
        const data: EditOrganizationData = {
          name: editingOrganization.name!,
          mode_allow_create: editingOrganization.mode_allow_create ?? false,
          mode_allow_translate:
            editingOrganization.mode_allow_translate ?? false,
          mode_allow_ai_generation:
            editingOrganization.mode_allow_ai_generation ?? false,
          mode_allow_translate_ai:
            editingOrganization.mode_allow_translate_ai ?? false,
        };
        await editOrganization(editingOrganization.id, data);
      } else {
        // Create new organization
        const data: CreateOrganizationData = {
          name: editingOrganization.name!,
          mode_allow_create: editingOrganization.mode_allow_create ?? false,
          mode_allow_translate:
            editingOrganization.mode_allow_translate ?? false,
          mode_allow_ai_generation:
            editingOrganization.mode_allow_ai_generation ?? false,
          mode_allow_translate_ai:
            editingOrganization.mode_allow_translate_ai ?? false,
        };
        await createOrganization(data);
      }

      onClose(true); // Refresh data
    } catch (error) {
      console.error("Error saving organization:", error);

      // Extract error message from backend response
      let errorMessage = "An error occurred while saving the organization";

      if (error instanceof AxiosError) {
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
      }

      // Show error on the name field since it's likely a duplicate name error
      setValidationErrors({
        name: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isEditMode && editingOrganization.id && onDelete) {
      onDelete(editingOrganization.id);
    }
  };

  const handleDisableEnable = () => {
    setShowDisableEnableConfirm(true);
  };

  const confirmDisableEnable = async () => {
    if (!organization?.id) return;

    const action = organization.disabled ? "enable" : "disable";

    try {
      if (organization.disabled) {
        await enableOrganization(organization.id);
      } else {
        await disableOrganization(organization.id);
      }

      if (onDisableEnable) {
        onDisableEnable(organization.id, !organization.disabled);
      }
      setShowDisableEnableConfirm(false);
      onClose(true);
    } catch (error) {
      console.error(`Failed to ${action} organization:`, error);
      setValidationErrors({
        general: `Failed to ${action} organization. Please try again.`,
      });
      setShowDisableEnableConfirm(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>
              {isEditMode ? "Edit Organization" : "Create Organization"}
            </span>
            {isEditMode && organization?.disabled && (
              <Chip label="Disabled" size="small" color="error" />
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Organization Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Organization Name"
                value={editingOrganization?.name || ""}
                onChange={(e) => handleFieldChange("name", e.target.value)}
                error={!!validationErrors.name}
                helperText={validationErrors.name || ""}
                required
              />
            </Grid>

            {/* Mode Checkboxes */}
            <Grid item xs={12}>
              <Box
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Modes
                </Typography>
                <Grid container spacing={2}>
                  {/* Left column - Create and AI modes */}
                  <Grid item xs={6}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              editingOrganization?.mode_allow_create || false
                            }
                            onChange={(e) =>
                              handleFieldChange(
                                "mode_allow_create",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="Create"
                      />
                      <Box
                        sx={{
                          position: "relative",
                          pl: "22px",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          Clients in this organization can create and access
                          batches
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          pl: "22px",
                          position: "relative",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: "9px",
                            top: "-52px",
                            bottom: "50%",
                            width: "1px",
                            backgroundColor: "#bdbdbd",
                          },
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            left: "9px",
                            top: "50%",
                            width: "12px",
                            height: "1px",
                            backgroundColor: "#bdbdbd",
                          },
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                editingOrganization?.mode_allow_ai_generation ||
                                false
                              }
                              onChange={(e) =>
                                handleFieldChange(
                                  "mode_allow_ai_generation",
                                  e.target.checked
                                )
                              }
                              disabled={!editingOrganization?.mode_allow_create}
                            />
                          }
                          label="Enable AI"
                        />
                      </Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              editingOrganization?.mode_allow_translate || false
                            }
                            onChange={(e) =>
                              handleFieldChange(
                                "mode_allow_translate",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="Translate"
                      />
                      <Box
                        sx={{
                          position: "relative",
                          pl: "22px",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          Clients can set human translation languages
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          pl: "22px",
                          position: "relative",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: "9px",
                            top: "-52px",
                            bottom: "50%",
                            width: "1px",
                            backgroundColor: "#bdbdbd",
                          },
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            left: "9px",
                            top: "50%",
                            width: "12px",
                            height: "1px",
                            backgroundColor: "#bdbdbd",
                          },
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                editingOrganization?.mode_allow_translate_ai ||
                                false
                              }
                              onChange={(e) =>
                                handleFieldChange(
                                  "mode_allow_translate_ai",
                                  e.target.checked
                                )
                              }
                              disabled={
                                !editingOrganization?.mode_allow_translate
                              }
                            />
                          }
                          label="AI Translation"
                        />
                        <Box
                          sx={{
                            position: "relative",
                            pl: "22px",
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 1 }}
                          >
                            Clients can set AI translation languages
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Right column - Review mode */}
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={<Checkbox checked={true} disabled />}
                      label="Review"
                    />
                    <Box
                      sx={{
                        position: "relative",
                        pl: "22px",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1 }}
                      >
                        Clients in this organization can always review and
                        approve content
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* General error message */}
            {validationErrors.general && (
              <Grid item xs={12}>
                <Box sx={{ color: "error.main", fontSize: "0.875rem" }}>
                  {validationErrors.general}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            {isEditMode && onDelete && (
              <Button onClick={handleDelete} color="error" variant="outlined">
                Delete
              </Button>
            )}
            {isEditMode && onDisableEnable && organization && (
              <Button
                onClick={handleDisableEnable}
                color={organization.disabled ? "success" : "error"}
                startIcon={
                  organization.disabled ? <CheckCircleIcon /> : <BlockIcon />
                }
              >
                {organization.disabled ? "Enable" : "Disable"}
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={() => onClose(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : isEditMode ? "Save" : "Create"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Disable/Enable Confirmation Dialog */}
      <Dialog
        open={showDisableEnableConfirm}
        onClose={() => setShowDisableEnableConfirm(false)}
      >
        <DialogTitle>
          {organization?.disabled
            ? "Enable Organization"
            : "Disable Organization"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {organization?.disabled
              ? "Are you sure you want to enable this organization? All users in this organization will be able to log in."
              : "Are you sure you want to disable this organization? This will prevent ALL users in this organization from logging in."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisableEnableConfirm(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmDisableEnable}
            color={organization?.disabled ? "success" : "error"}
            variant="contained"
          >
            {organization?.disabled ? "Enable" : "Disable"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
